/**
 * OfflineSyncService.ts
 *
 * IndexedDB-backed offline queue.
 *
 * CORRECT FLOW:
 *  Online  → uploads go directly to backend → AnalysisResultUI → Firebase
 *  Offline → raw payload saved to IndexedDB (no backend call)
 *  Reconnect → syncPending() resends each item to its backend endpoint,
 *              gets Gemini response, then calls saveToFirebase() to write
 *              the result into onboardingData/{autokey} — same path as
 *              the normal online flow through SurveyService.
 *
 * KEY FIX — DataCloneError:
 *  IndexedDB structuredClone cannot store Promises, Functions, or DOM nodes.
 *  The payload passed to enqueue() must be a plain serialisable object.
 *  DirectUpload must call saveData() functions synchronously and
 *  collect their RETURN VALUES (plain objects), not the Promises they also
 *  return for Firebase writes.
 *
 * KEY FIX — Race condition:
 *  Module-level isSyncingInternal boolean prevents two sync loops running
 *  simultaneously if "Sync Now" and the auto-reconnect fire at the same time.
 *
 * KEY FIX — 401 on sync replay:
 *  Firebase ID tokens expire after 1 hour. Tokens cannot be stored at queue
 *  time and reused later. Instead, getSyncAuthHeader() fetches a fresh token
 *  (with forceRefresh: true) at the moment each queued item is replayed.
 */

import { ref, push } from "firebase/database";
import { database, auth as firebaseAuth } from "../firebase";
import { sanitizeRecord } from "./PrivacyService";

const DB_NAME    = "kith_offline";
const DB_VERSION = 1;
const STORE      = "pending_surveys";

export interface PendingSurvey {
  id:         string;
  payload:    Record<string, any>;   // MUST be a plain serialisable object
  uploadType: "bulk_upload" | "direct_upload" | "picture_upload";
  endpoint:   string;
  timestamp:  string;
  retries:    number;
  label:      string;
  userId:     string;               // needed to write to Firebase on sync
}

export type SyncItemStatus = "syncing" | "done" | "failed" | "skipped";

// ── Module-level sync lock ────────────────────────────────────────────────────
let isSyncingInternal = false;

// ── Pub/sub — instant panel update ───────────────────────────────────────────
type QueueListener = () => void;
const listeners = new Set<QueueListener>();

export function onQueueChange(cb: QueueListener): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function notifyListeners(): void {
  listeners.forEach(cb => { try { cb(); } catch {} });
}

// ── Auth token helper (fresh token at replay time) ────────────────────────────
// Called at the moment each queued item is replayed — never at queue time —
// so the token is always fresh. forceRefresh: true bypasses the local cache,
// which is important for items that have been queued for more than ~1 hour.
async function getSyncAuthHeader(): Promise<Record<string, string>> {
  const user = firebaseAuth.currentUser;
  if (!user) return {};
  try {
    const token = await user.getIdToken(/* forceRefresh */ true);
    return { Authorization: `Bearer ${token}` };
  } catch {
    return {};
  }
}

// ── DB ────────────────────────────────────────────────────────────────────────
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = e => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = e => resolve((e.target as IDBOpenDBRequest).result);
    req.onerror   = e => reject((e.target as IDBOpenDBRequest).error);
  });
}

// ── CRUD ──────────────────────────────────────────────────────────────────────
export async function enqueue(
  survey: Omit<PendingSurvey, "id" | "timestamp" | "retries">
): Promise<string> {
  // Safety: strip any non-serialisable values so structuredClone doesn't throw
  const safePayload = JSON.parse(JSON.stringify(survey.payload ?? {}));

  const db = await openDB();
  const record: PendingSurvey = {
    ...survey,
    payload:   safePayload,
    id:        crypto.randomUUID(),
    timestamp: new Date().toISOString(),
    retries:   0,
  };

  await new Promise<void>((resolve, reject) => {
    const tx  = db.transaction(STORE, "readwrite");
    const req = tx.objectStore(STORE).add(record);
    req.onsuccess = () => resolve();
    req.onerror   = e => reject((e.target as IDBRequest).error);
  });

  notifyListeners();
  return record.id;
}

export async function getAll(): Promise<PendingSurvey[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = e => resolve((e.target as IDBRequest<PendingSurvey[]>).result ?? []);
    req.onerror   = e => reject((e.target as IDBRequest).error);
  });
}

export async function remove(id: string): Promise<void> {
  const db = await openDB();
  await new Promise<void>((resolve, reject) => {
    const tx  = db.transaction(STORE, "readwrite");
    const req = tx.objectStore(STORE).delete(id);
    req.onsuccess = () => resolve();
    req.onerror   = e => reject((e.target as IDBRequest).error);
  });
  notifyListeners();
}

export async function incrementRetry(id: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx    = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    const getReq = store.get(id);
    getReq.onsuccess = e => {
      const record: PendingSurvey = (e.target as IDBRequest).result;
      if (!record) { resolve(); return; }
      record.retries += 1;
      store.put(record).onsuccess = () => resolve();
    };
    getReq.onerror = e => reject((e.target as IDBRequest).error);
  });
}

// ── Save Gemini result to Firebase (same path as online flow) ─────────────────
async function saveResultToFirebase(
  geminiResult: Record<string, any>,
  originalPayload: Record<string, any>,
  uploadType: string,
  userId: string,
  locationString: string,
  lat: number,
  lng: number
): Promise<boolean> {
  try {
    const sanitized = sanitizeRecord({
      ...originalPayload,
      ...geminiResult,
      location:     locationString,
      lat,
      lng,
      sourceMethod: uploadType,
      savedAt:      new Date().toISOString(),
    });
    await push(ref(database, `users/${userId}/onboardingData`), sanitized);
    console.log(`[OfflineSync] ✅ Saved to Firebase: users/${userId}/onboardingData`);
    return true;
  } catch (e) {
    console.error("[OfflineSync] ❌ Firebase write failed:", e);
    return false;
  }
}

// ── Geocode helper ────────────────────────────────────────────────────────────
function geocode(address: string): Promise<{ lat: number; lng: number } | null> {
  return new Promise(resolve => {
    if (!window.google?.maps) { resolve(null); return; }
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address }, (results, status) => {
      if (status === "OK" && results?.[0]?.geometry?.location) {
        resolve({
          lat: results[0].geometry.location.lat(),
          lng: results[0].geometry.location.lng(),
        });
      } else {
        resolve(null);
      }
    });
  });
}

// ── syncPending ───────────────────────────────────────────────────────────────
/**
 * For each queued item:
 *  1. Fetch a fresh auth token (forceRefresh: true — stored tokens may be stale)
 *  2. Send the payload to the backend (same endpoint as the online path)
 *  3. Get the Gemini result
 *  4. Geocode the location in the result
 *  5. Write to Firebase onboardingData/{autokey} — same as AnalysisResultUI does online
 *  6. Remove from IndexedDB
 *
 * bulk_upload items are skipped — file bytes can't be stored in IndexedDB.
 */
export async function syncPending(
  onProgress?: (id: string, status: SyncItemStatus) => void
): Promise<number> {
  if (!navigator.onLine) return 0;
  if (isSyncingInternal) return 0;

  isSyncingInternal = true;
  let synced = 0;

  try {
    const pending = await getAll();

    for (const survey of pending) {
      if (survey.uploadType === "bulk_upload") {
        onProgress?.(survey.id, "skipped");
        continue;
      }

      onProgress?.(survey.id, "syncing");

      try {
        // ── Step 1: build form data ──────────────────────────────────────
        const formData = new FormData();

        if (survey.uploadType === "picture_upload" && survey.payload?.file) {
          formData.append("image", survey.payload.file);
        } else {
          formData.append("survey_text", JSON.stringify(survey.payload));
        }

        // ── Step 2: fetch a fresh token and send to backend ──────────────
        // Token is fetched here — not at queue time — because Firebase ID
        // tokens expire after 1 hour and queued items may be replayed much
        // later. forceRefresh: true bypasses the local cache entirely.
        const authHeader = await getSyncAuthHeader();

        const res = await fetch(survey.endpoint, {
          method:  "POST",
          headers: authHeader,
          body:    formData,
          signal:  AbortSignal.timeout(90000),
        });

        if (!res.ok) {
          console.warn(`[OfflineSync] ❌ ${res.status} for survey ${survey.id}`);
          await incrementRetry(survey.id);
          onProgress?.(survey.id, "failed");
          continue;
        }

        const data = await res.json();
        if (!data.success) {
          await incrementRetry(survey.id);
          onProgress?.(survey.id, "failed");
          continue;
        }

        // ── Step 3: extract location from Gemini result ──────────────────
        const locationString =
          data.location ||
          survey.payload?.method?.address ||
          survey.payload?.method?.communityLocation ||
          null;

        if (!locationString) {
          // No location — remove from queue but don't write to Firebase
          await remove(survey.id);
          onProgress?.(survey.id, "done");
          synced++;
          continue;
        }

        // ── Step 4: geocode ──────────────────────────────────────────────
        const coords = await geocode(locationString);
        if (!coords) {
          // Geocoding failed — remove anyway, data is lost (no coords to map)
          await remove(survey.id);
          onProgress?.(survey.id, "done");
          synced++;
          continue;
        }

        // ── Step 5: write to Firebase onboardingData ─────────────────────
        await saveResultToFirebase(
          data,
          survey.payload,
          survey.uploadType,
          survey.userId,
          locationString,
          coords.lat,
          coords.lng
        );

        // ── Step 5.5: notify the UI ──────────────────────────────────────
        // Tells DirectUpload to switch from "offline mode" to "success mode"
        // and render AnalysisResultUI inline.
        window.dispatchEvent(new CustomEvent("kith:analysis-ready", {
          detail: {
            result:     data,
            uploadType: survey.uploadType,
          },
        }));

        // ── Step 6: remove from queue ────────────────────────────────────
        await remove(survey.id);
        onProgress?.(survey.id, "done");
        synced++;

      } catch (e) {
        console.error("[OfflineSync] Error syncing item:", survey.id, e);
        await incrementRetry(survey.id);
        onProgress?.(survey.id, "failed");
      }
    }
  } finally {
    isSyncingInternal = false;
  }

  return synced;
}

// ── Connectivity ──────────────────────────────────────────────────────────────
export function isOnline(): boolean {
  return navigator.onLine;
}

export function watchConnectivity(cb: (online: boolean) => void): () => void {
  const on  = () => cb(true);
  const off = () => cb(false);
  window.addEventListener("online",  on);
  window.addEventListener("offline", off);
  cb(navigator.onLine);
  return () => {
    window.removeEventListener("online",  on);
    window.removeEventListener("offline", off);
  };
}
